import React, { useCallback, useEffect, useMemo } from 'react';

import { useApplicationState, useCasinoNewState, useCategoryGamesState } from '@atlas/core';

import { useCasinoFilterState } from 'components/Pages/Casino/CasinoFilterContext';
import {
  casinoFilterTypeMap,
  deleteCasinoFilter,
} from 'src/components/Pages/Casino/useCasinoFilter';

import { CasinoCategory } from './CasinoCategory';
import { GAME_SIZES, GROUPS } from './constants';

const casinoSliderSettings = [
  { rows: 1, size: GAME_SIZES.MEDIUM },
  { rows: 1, size: GAME_SIZES.LARGE },
];

const defaultCasinoSliderSettings = { rows: 1, size: GAME_SIZES.SMALL };

const liveCasinoSliderSettings = { rows: 1, size: GAME_SIZES.SMALL };

const sliderSettingsByGroup = {
  [GROUPS.CASINO]: index => casinoSliderSettings[index] || defaultCasinoSliderSettings,
  [GROUPS.LIVE]: index => casinoSliderSettings[index] || liveCasinoSliderSettings,
};

const HomePageGames = ({ InfoGamesList, scrollParent, setActiveItem }) => {
  const { group, identifyFavoriteGames } = useCasinoNewState();
  const { categoryGames } = useCategoryGamesState();
  const { authenticated } = useApplicationState();
  const { updateFilter } = useCasinoFilterState();

  useEffect(() => {
    if (!authenticated || !categoryGames.length) return;

    const gameIds = categoryGames.flatMap(({ games }) => games.map(({ id }) => id));
    identifyFavoriteGames(gameIds);
  }, [categoryGames, authenticated, identifyFavoriteGames]);

  const sortedCategoriesWithGames = useMemo(() => {
    return [...categoryGames].sort((a, b) => a.position - b.position);
  }, [categoryGames]);

  const showCategoryHandler = useCallback(
    categoryId => () => {
      updateFilter({ providerNames: [] });
      deleteCasinoFilter(casinoFilterTypeMap.providerNames);
      setActiveItem(categoryId.toString());
      scrollParent()?.scrollTo(0, 0);
    },
    [scrollParent, setActiveItem, updateFilter],
  );

  const getSliderSettings = useCallback(
    index => sliderSettingsByGroup[group]?.(index) || defaultCasinoSliderSettings,
    [group],
  );

  return (
    <div className="casino-category-wrapper">
      {sortedCategoriesWithGames.map(({ games, iconUrl, id, name }, index) => (
        <CasinoCategory
          InfoGamesList={InfoGamesList}
          games={games}
          iconUrl={iconUrl}
          id={id}
          isImgFetchHighPriority={index < 2} // set to true for first 2 sets of games which are at first screen (for LCP)
          isImgLoadingEager={index < 2}
          key={id}
          name={name}
          showCategoryHandler={showCategoryHandler}
          sliderSettings={getSliderSettings(index)}
        />
      ))}
    </div>
  );
};

export default HomePageGames;
